import React, { useState, useEffect } from 'react';
import { userReviewAPI } from '../../services/api';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import ReviewPrompt from './ReviewPrompt';
import './ReviewsList.css';

const ReviewsList = ({ userId, currentUserId, isOwnProfile }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [ratingDistribution, setRatingDistribution] = useState({});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [myReview, setMyReview] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchReviews();
      if (currentUserId && !isOwnProfile) {
        fetchMyReview();
      }
    }
  }, [userId, currentUserId, isOwnProfile]);

  const fetchReviews = async (pageNum = 1) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await userReviewAPI.getReviews(userId, { per_page: 10, page: pageNum });
      
      if (response.success) {
        if (pageNum === 1) {
          setReviews(response.data.reviews);
        } else {
          setReviews(prev => [...prev, ...response.data.reviews]);
        }
        
        setAverageRating(response.data.average_rating || 0);
        setTotalReviews(response.data.total_reviews || 0);
        setRatingDistribution(response.data.rating_distribution || {});
        setHasMore(response.data.pagination.current_page < response.data.pagination.last_page);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchMyReview = async () => {
    try {
      const response = await userReviewAPI.getMyReview(userId);
      if (response.success && response.data) {
        setMyReview(response.data);
      }
    } catch (err) {
      console.error('Error fetching my review:', err);
    }
  };

  const handleReviewSubmitted = () => {
    setShowReviewForm(false);
    fetchReviews(1);
    fetchMyReview();
  };

  const handleReviewUpdated = () => {
    setShowReviewForm(false);
    fetchReviews(1);
    fetchMyReview();
  };

  const handleReviewDeleted = async () => {
    if (myReview && window.confirm('Are you sure you want to delete your review?')) {
      try {
        const response = await userReviewAPI.deleteReview(myReview.id);
        if (response.success) {
          setMyReview(null);
          fetchReviews(1);
        } else {
          alert(response.message || 'Failed to delete review');
        }
      } catch (err) {
        console.error('Error deleting review:', err);
        alert('Failed to delete review. Please try again.');
      }
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchReviews(page + 1);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="content-card reviews-list-container">
        <h3 className="reviews-title">Reviews & Recommendations</h3>
        <div className="reviews-loading">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div className="content-card reviews-list-container">
      <h3 className="reviews-title">Reviews & Recommendations</h3>
      {totalReviews > 0 && (
        <div className="reviews-stats">
          <div className="average-rating">
            <span className="rating-value">{averageRating.toFixed(1)}</span>
            <div className="rating-stars">{renderStars(Math.round(averageRating))}</div>
            <span className="rating-count">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
          </div>
        </div>
      )}

      {/* Engaging Review Prompt */}
      {!isOwnProfile && currentUserId && !showReviewForm && (
        <ReviewPrompt
          totalReviews={totalReviews}
          averageRating={averageRating}
          hasReviewed={!!myReview}
          onWriteReview={() => setShowReviewForm(true)}
          onEditReview={() => setShowReviewForm(true)}
          onDeleteReview={handleReviewDeleted}
        />
      )}

      {showReviewForm && (
        <ReviewForm
          userId={userId}
          existingReview={myReview}
          onSubmitted={handleReviewSubmitted}
          onUpdated={handleReviewUpdated}
          onCancel={() => setShowReviewForm(false)}
        />
      )}

      {error && (
        <div className="reviews-error">{error}</div>
      )}

      {totalReviews === 0 && !showReviewForm ? (
        <div className="reviews-empty">
          <p>No reviews yet. {!isOwnProfile && currentUserId && 'Be the first to write a review!'}</p>
        </div>
      ) : (
        <>
          <div className="reviews-list">
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                currentUserId={currentUserId}
                onUpdated={handleReviewUpdated}
                onDeleted={handleReviewDeleted}
              />
            ))}
          </div>

          {hasMore && (
            <div className="reviews-load-more">
              <button 
                className="btn-load-more"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More Reviews'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsList;
